import { useState, useEffect, useRef } from 'react';
import AssetQRModal from './AssetQRModal';
import AssetProfileModal from './AssetProfileModal';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { assetsAPI, usersAPI, workOrdersAPI } from '../../api';
import toast from 'react-hot-toast';
import styles from './Assets.module.css';
import AssetImportExport from "./AssetImportExport";
const fmt = n => n ? '₹' + Number(n).toLocaleString('en-IN', { minimumFractionDigits: 0 }) : '—';

const STATUS_COLORS = {
  operational: { bg:'#dcfce7', color:'#15803d' },
  maintenance:  { bg:'#fef9c3', color:'#a16207' },
  breakdown:    { bg:'#fee2e2', color:'#b91c1c' },
  retired:      { bg:'#f1f5f9', color:'#64748b' },
  disposed:     { bg:'#f1f5f9', color:'#94a3b8' },
};

const STATUS_FILTERS = [
  { key:'',            label:'All Assets' },
  { key:'operational', label:'Operational' },
  { key:'maintenance', label:'Maintenance' },
  { key:'breakdown',   label:'Breakdown'  },
  { key:'retired',     label:'Retired'    },
];

// ── Add Asset Modal ───────────────────────────────────────────
function AddAssetModal({ categories, locations, onClose, onSave }) {
  const [form, setForm] = useState({ name:'', asset_code:'', category_id:'', location_id:'', department:'', status:'operational', make:'', model:'', serial_number:'', purchase_cost:'', description:'', is_critical:false });
  const [saving, setSaving] = useState(false);
  const set = (k,v) => setForm(f => ({...f,[k]:v}));

  const submit = async () => {
    if (!form.name || !form.asset_code) { toast.error('Name and code required'); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k,v]) => { if (v !== '') fd.append(k, v); });
      const r = await assetsAPI.create(fd);
      toast.success('Asset created');
      onSave(r.data.id);
    } catch(e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  return (
    <div className={styles.modal_overlay} onClick={e => e.target === e.currentTarget}>
      <div className={styles.modal} onClick={e=>e.stopPropagation()}>
        <div className={styles.modal_header}><h2 id="staticBackdropLabel">New Asset</h2><button onClick={onClose} className={styles.modal_close}>✕</button></div>
        <div className={styles.modal_body}>
          <div className={styles.form_grid}>
            <div className={styles.form_group} style={{gridColumn:'1/-1'}}><label>Asset Name *</label><input value={form.name} onChange={e=>set('name',e.target.value)} placeholder="e.g. CNC Machining Center #1"/></div>
            <div className={styles.form_group}><label>Asset Code *</label><input value={form.asset_code} onChange={e=>set('asset_code',e.target.value)} placeholder="e.g. AST-CNC-001"/></div>
            <div className={styles.form_group}><label>Status</label><select value={form.status} onChange={e=>set('status',e.target.value)}><option value="operational">Operational</option><option value="maintenance">Maintenance</option><option value="breakdown">Breakdown</option><option value="retired">Retired</option></select></div>
            <div className={styles.form_group}><label>Category</label><select value={form.category_id} onChange={e=>set('category_id',e.target.value)}><option value="">Select category</option>{categories.map(c=><option key={c.id} value={c.id}>{c.icon} {c.name}</option>)}</select></div>
            <div className={styles.form_group}><label>Location</label><select value={form.location_id} onChange={e=>set('location_id',e.target.value)}><option value="">Select location</option>{locations.map(l=><option key={l.id} value={l.id}>{l.name}</option>)}</select></div>
            <div className={styles.form_group}><label>Make / Brand</label><input value={form.make} onChange={e=>set('make',e.target.value)} placeholder="e.g. Makino"/></div>
            <div className={styles.form_group}><label>Model</label><input value={form.model} onChange={e=>set('model',e.target.value)}/></div>
            <div className={styles.form_group}><label>Serial Number</label><input value={form.serial_number} onChange={e=>set('serial_number',e.target.value)}/></div>
            <div className={styles.form_group}><label>Department</label><input value={form.department} onChange={e=>set('department',e.target.value)}/></div>
            <div className={styles.form_group}><label>Purchase Cost (₹)</label><input type="number" value={form.purchase_cost} onChange={e=>set('purchase_cost',e.target.value)} placeholder="0"/></div>
            <div className={styles.form_group} style={{gridColumn:'1/-1'}}><label>Description</label><textarea value={form.description} onChange={e=>set('description',e.target.value)} rows={2} placeholder="Additional details…"/></div>
            <div className={styles.form_group}><label className={styles.checkbox_label}><input type="checkbox" checked={form.is_critical} onChange={e=>set('is_critical',e.target.checked)}/>Mark as Critical Asset</label></div>
          </div>
        </div>
        <div className={styles.modal_footer}>
          <button className={styles.btn_cancel} onClick={onClose}>Cancel</button>
          <button className={styles.btn_submit} onClick={submit} disabled={saving}>{saving?'Saving…':'Create Asset'}</button>
        </div>
      </div>
    </div>
  );
}

// ── Raise WO Modal ────────────────────────────────────────────
function RaiseWOModal({ asset, onClose, onSave }) {
  const [form, setForm] = useState({ title:'', type:'breakdown', priority:'medium', description:'', due_date:'' });
  const [saving, setSaving] = useState(false);
  const set = (k,v) => setForm(f=>({...f,[k]:v}));

  const submit = async () => {
    if (!form.title) { toast.error('Title required'); return; }
    setSaving(true);
    try { await workOrdersAPI.create({...form, asset_id: asset.id}); toast.success('Work order raised'); onSave(); }
    catch(e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  return (
    <div className={styles.modal_overlay} onClick={e => e.target === e.currentTarget}>
      <div className={styles.modal} style={{width:480}} onClick={e=>e.stopPropagation()}>
        <div className={styles.modal_header}><h2 id="staticBackdropLabel">Raise Work Order</h2><button onClick={onClose} className={styles.modal_close}>✕</button></div>
        <div className={styles.modal_body}>
          <div style={{background:'#f0f4ff',borderRadius:8,padding:'10px 14px',marginBottom:16,fontSize:13,color:'#1e2a3b'}}>Asset: <strong>{asset.name}</strong> ({asset.asset_code})</div>
          <div className={styles.form_grid}>
            <div className={styles.form_group} style={{gridColumn:'1/-1'}}><label>Title *</label><input value={form.title} onChange={e=>set('title',e.target.value)} placeholder="Describe the issue"/></div>
            <div className={styles.form_group}><label>Type</label><select value={form.type} onChange={e=>set('type',e.target.value)}><option value="breakdown">Breakdown</option><option value="pm">Preventive Maintenance</option><option value="repair">Repair</option><option value="inspection">Inspection</option><option value="calibration">Calibration</option></select></div>
            <div className={styles.form_group}><label>Priority</label><select value={form.priority} onChange={e=>set('priority',e.target.value)}><option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option><option value="critical">Critical</option></select></div>
            <div className={styles.form_group}><label>Due Date</label><input type="date" value={form.due_date} onChange={e=>set('due_date',e.target.value)}/></div>
            <div className={styles.form_group} style={{gridColumn:'1/-1'}}><label>Description</label><textarea value={form.description} onChange={e=>set('description',e.target.value)} rows={3} placeholder="Details…"/></div>
          </div>
        </div>
        <div className={styles.modal_footer}>
          <button className={styles.btn_cancel} onClick={onClose}>Cancel</button>
          <button className={styles.btn_submit} onClick={submit} disabled={saving}>{saving?'Saving…':'Raise Work Order'}</button>
        </div>
      </div>
    </div>
  );
}

// ── Detail Panel ──────────────────────────────────────────────
function DetailPanel({ asset, onEdit, onRaiseWO, onQR, onViewProfile }) {
  const navigate = useNavigate();
  if (!asset) return <div className={styles.detail_empty}>Select an asset to view details</div>;
  const sc = STATUS_COLORS[asset.status] || STATUS_COLORS.operational;

  return (
    <div className={styles.detail_panel}>
      <div className={styles.dp_header}>
        <div className={styles.dp_code}>{asset.asset_code}</div>
        <span className={styles.dp_status} style={{background:sc.bg,color:sc.color}}>{asset.status}</span>
      </div>
      <div className={styles.dp_name}>{asset.name}</div>
      {asset.is_critical && <div className={styles.dp_critical}>⚡ Critical Asset</div>}
      {asset.description && <p className={styles.dp_desc}>{asset.description}</p>}

      <div className={styles.dp_actions}>
      {/*   <button className={styles.view_full_btnraised} onClick={onRaiseWO}>🔧 Raise Work Order</button> */}
        <button className={styles.view_full_btnraised} onClick={onEdit}>✏ Edit</button>
        <button className={styles.view_full_btnQR} onClick={onQR} title="Generate QR Code">🔲 QR</button>
      </div>

      <div className={styles.dp_divider}/>
      <div className={styles.dp_section}>Asset Details</div>
      {[['Make',asset.make],['Model',asset.model],['Serial No.',asset.serial_number],['Category',asset.category_name],['Location',asset.location_name],['Department',asset.department],['Assigned To',asset.assigned_user_name],['Purchase Cost',asset.purchase_cost?fmt(asset.purchase_cost):null],['Purchase Date',asset.purchase_date?.slice(0,10)],['Warranty Expiry',asset.warranty_expiry?.slice(0,10)],['Added On',asset.created_at?.slice(0,10)]].filter(([,v])=>v).map(([l,v])=>(
      <div key={l} className={styles.dp_row}><span>{l}</span><strong>{v}</strong></div>
      ))}

      {asset.work_orders?.length > 0 && (<>
        <div className={styles.dp_divider}/>
        <div className={styles.dp_section}>Recent Work Orders</div>
        {asset.work_orders.slice(0,4).map(wo=>(
          <div key={wo.id} className={styles.dp_wo_row} onClick={()=>navigate(`/work-orders/${wo.id}`)} style={{cursor:'pointer'}}>
            <span className={styles.dp_wo_num}>{wo.wo_number}</span>
            <span className={styles.dp_wo_type}>{wo.type}</span>
            <span className={styles.dp_wo_status} style={{background:wo.status==='closed'?'#dcfce7':wo.status==='open'?'#fff7ed':'#eff4ff',color:wo.status==='closed'?'#15803d':wo.status==='open'?'#c2410c':'#1a56db'}}>{wo.status}</span>
          </div>
        ))}
      </>)}

      {asset.pm_schedules?.length > 0 && (<>
        <div className={styles.dp_divider}/>
        <div className={styles.dp_section}>PM Schedules</div>
        {asset.pm_schedules.map(pm=>(
          <div key={pm.id} className={styles.dp_pm_row}>
            <div className={styles.dp_pm_title}>{pm.title}</div>
            <div className={styles.dp_pm_meta}>{pm.frequency_type} · Next: {pm.next_due?.slice(0,10)||'—'}</div>
          </div>
        ))}
      </>)}
      <button className={styles.view_full_btn} onClick={()=>navigate(`/assets/${asset.id}/profile`)}>
        View Full Asset Profile →
      </button>
      
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────────
export default function AssetsList() {
  const navigate             = useNavigate();
  const [searchParams]       = useSearchParams();
  const [assets,   setAssets]= useState([]);
  const [categories,setCategories]=useState([]);
  const [locations, setLocations] =useState([]);
  const [loading,  setLoading]    =useState(true);
  const [total,    setTotal]      =useState(0);
  const [selected, setSelected]   =useState(null);
  const [selectedFull,setSelectedFull]=useState(null);
  const [detailLoading,setDetailLoading]=useState(false);
  const [showAdd,  setShowAdd]    =useState(false);
  const [profileAssetId, setProfileAssetId] =useState(null);
  const [showNewAssetModal,setShowNewAssetModal] = useState(false);   // ← ADD
  const [showWO,   setShowWO]     =useState(false);
  const [qrAsset,  setQrAsset]    =useState(null);
  const [showDrop, setShowDrop]   =useState(false);
  const dropRef = useRef();
  const [filters, setFilters] = useState({ q:searchParams.get('q')||'', status:'', category_id:'', location_id:'' });

  useEffect(()=>{
    const h=e=>{ if(dropRef.current&&!dropRef.current.contains(e.target)) setShowDrop(false); };
    document.addEventListener('mousedown',h); return ()=>document.removeEventListener('mousedown',h);
  },[]);

  useEffect(()=>{
    assetsAPI.categories().then(r=>setCategories(r.data)).catch(()=>{});
    usersAPI.locations().then(r=>setLocations(r.data.data)).catch(()=>{});
  },[]);

  const loadAssets = () => {
    setLoading(true);
    const p=Object.fromEntries(Object.entries(filters).filter(([,v])=>v));
    assetsAPI.list(p).then(r=>{setAssets(r.data.data);setTotal(r.data.total);}).catch(console.error).finally(()=>setLoading(false));
  };
  useEffect(()=>{ loadAssets(); },[filters]);

  useEffect(()=>{
    if(!selected){setSelectedFull(null);return;}
    setDetailLoading(true);
    assetsAPI.get(selected.id).then(r=>setSelectedFull(r.data)).catch(()=>setSelectedFull(selected)).finally(()=>setDetailLoading(false));
  },[selected]);

  const upd = (k,v) => setFilters(f=>({...f,[k]:v}));
  const countByStatus = assets.reduce((acc,a)=>({...acc,[a.status]:(acc[a.status]||0)+1}),{});

  return (
    <div className={styles.page}>
      {/* Toolbar */}
      <div className={styles.toolbar}>
        <div className={styles.new_btn_wrap} ref={dropRef}>
          <button className={styles.new_btn_main} onClick={()=>navigate('/assets/new')}>New</button>
          <button className={styles.new_btn_arrow} onClick={()=>setShowDrop(d=>!d)}>▾</button>
          {showDrop&&(<div className={styles.new_dropdown}>
            <div className={styles.new_dropdown_item} onClick={()=>{navigate('/assets/new');setShowDrop(false);}}>⚙ New Asset</div>
            <div className={styles.new_dropdown_item} onClick={()=>{navigate('/assets/new');setShowDrop(false);}}>📋 Advanced Form</div>
          </div>)}
        </div>
        <div className={styles.search_wrap}>
          <span className={styles.search_icon}><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg></span>
          <input className={styles.search_input} type="text" placeholder="Search assets by name, code, serial number…" value={filters.q} onChange={e=>upd('q',e.target.value)}/>
          {filters.q&&<button className={styles.search_clear} onClick={()=>upd('q','')}>✕</button>}
        </div>
        <select className={styles.toolbar_select} value={filters.category_id} onChange={e=>upd('category_id',e.target.value)}>
          <option value="">All Categories</option>
          {categories.map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <select className={styles.toolbar_select} value={filters.location_id} onChange={e=>upd('location_id',e.target.value)}>
          <option value="">All Locations</option>
          {locations.map(l=><option key={l.id} value={l.id}>{l.name}</option>)}
        </select>
        <div className={styles.toolbar_right}><span className={styles.total_badge}>{total} assets</span></div>
      </div>

      {/* Body */}
      <div className={styles.body}>
        {/* Left sidebar */}
        <div className={styles.filter_col}>
          {STATUS_FILTERS.map(f=>{
            const cnt=f.key?(countByStatus[f.key]||0):total;
            return(
            <div key={f.key} className={`${styles.filter_item} ${filters.status===f.key?styles.filter_active:''}`} onClick={()=>upd('status',f.key)}>
              <span className={styles.filter_dot} style={{background:f.key?STATUS_COLORS[f.key]?.color:'#1a56db'}}/>
              <span className={styles.filter_label}>{f.label}</span>
              <span className={`${styles.filter_count} ${filters.status===f.key?styles.filter_count_active:''}`}>{cnt}</span>
            </div>);
          })}
          <div className={styles.filter_divider}/>
          <div className={styles.filter_section}>By Category</div>
          {categories.map(c=>(
            <div key={c.id} className={`${styles.filter_item} ${filters.category_id===String(c.id)?styles.filter_active:''}`} onClick={()=>upd('category_id',filters.category_id===String(c.id)?'':String(c.id))}>
              <span style={{fontSize:14}}>{c.icon}</span>
              <span className={styles.filter_label}>{c.name}</span>
            </div>
          ))}
          {Object.values(filters).some(Boolean)&&<button className={styles.reset_btn} onClick={()=>setFilters({q:'',status:'',category_id:'',location_id:''})}>✕ Reset Filters</button>}
        </div>

        {/* Center list */}
        <div className={styles.list_col}>
          {loading?<div className={styles.loading}>Loading assets…</div>
          :assets.length===0?<div className={styles.empty}><div style={{fontSize:40,marginBottom:12}}>🏭</div><div style={{fontWeight:600,color:'#1e2a3b',marginBottom:6}}>No assets found</div><div style={{fontSize:13,color:'#94a3b8'}}>Try changing filters or add a new asset</div></div>
          :assets.map(asset=>{
            const sc=STATUS_COLORS[asset.status]||STATUS_COLORS.operational;
            const isSel=selected?.id===asset.id;
            return(
            <div key={asset.id} className={`${styles.card} ${isSel?styles.card_active:''}`} onClick={()=>setSelected(asset)}>
              <div className={styles.card_stripe} style={{background:sc.color}}/>
              <div className={styles.card_body}>
                <div className={styles.card_top}>
                  <div className={styles.card_code}>{asset.asset_code}</div>
                  <span className={styles.card_status} style={{background:sc.bg,color:sc.color}}>{asset.status}</span>
                </div>
                <div className={styles.card_name}>{asset.name}{asset.is_critical&&<span className={styles.card_critical}>⚡ Critical</span>}</div>
                <div className={styles.card_meta}>{[asset.category_name,asset.make,asset.model].filter(Boolean).join(' · ')}</div>
                <div className={styles.card_footer}>
                  <span className={styles.card_loc}><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>{asset.location_name||'—'}</span>
                  {asset.purchase_cost&&<span className={styles.card_cost}>{fmt(asset.purchase_cost)}</span>}
                </div>
              </div>
              <div className={styles.card_actions}>
                <button className={styles.card_action_btn} title="View" onClick={e=>{e.stopPropagation();navigate(`/assets/${asset.id}`);}}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                </button>
                <button className={styles.card_action_btn} title="Edit" onClick={e=>{e.stopPropagation();navigate(`/assets/${asset.id}/edit`);}}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                </button>
                <button className={styles.card_action_btn} title="Raise WO" onClick={e=>{e.stopPropagation();setSelected(asset);setShowWO(true);}}>🔧</button>
              </div>
            </div>);
          })}
        </div>

        {/* Right detail */}
        <div className={styles.detail_col}>
          {detailLoading?<div className={styles.detail_loading}>Loading…</div>
          :<DetailPanel asset={selectedFull||selected} onEdit={()=>selectedFull&&navigate(`/assets/${selectedFull.id}/edit`)} onRaiseWO={()=>setShowWO(true)} onQR={()=>setQrAsset(selectedFull||selected)} onViewProfile={(id)=>navigate(`/assets/${id}/profile`)}/>}
        </div>
      </div>

      {showAdd&&<AddAssetModal categories={categories} locations={locations} onClose={()=>setShowAdd(false)} onSave={id=>{setShowAdd(false);navigate(`/assets/${id}/profile`);}}/>}
      {showWO&&selected&&<RaiseWOModal asset={selected} onClose={()=>setShowWO(false)} onSave={()=>setShowWO(false)}/>
      }
      {qrAsset&&<AssetQRModal asset={qrAsset} onClose={()=>setQrAsset(null)}/>}
    </div>
  );
}

function AssetsPage() {
     return (
       <div>
         <h1>Assets</h1>
         <AssetImportExport />
         {/* your existing asset table below */}
       </div>
     );
   }